import React from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import { useDarkMode } from 'Hooks';
import { getReactSelectStyles } from 'Helpers/UiHelper';
import Select from 'react-select';

const FlightCostSection = ({
  currentSelectedFlight,
  setCurrentSelectedFlight,
  validationFlightFormErrors,
  setValidationFlightFormErrors,
  validLuggageSelectOptions,
  costCentreOptions,
  currencySelectOptions,
  totalCost,
  isDisabled,
  isHotelDisabled,
}) => {
  const { darkMode } = useDarkMode();
  const safeNumberValue = (val) => (val === undefined || val === null || val === '' ? '' : val);

  return (
    <>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>Luggagee</Form.Label>
          <Select
            isDisabled={isDisabled}
            options={validLuggageSelectOptions}
            isSearchable
            isClearable
            value={
              validLuggageSelectOptions.find((x) => x.value === currentSelectedFlight.luggage) ||
              (currentSelectedFlight.luggage
                ? { value: currentSelectedFlight.luggage, label: currentSelectedFlight.luggage }
                : null)
            }
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                luggage: e?.value || '',
              });
            }}
            styles={getReactSelectStyles(darkMode)}
          />
          {validationFlightFormErrors.luggage && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {validationFlightFormErrors.luggage}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Xbag Cost</Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.5"
            value={safeNumberValue(currentSelectedFlight?.xbagCost)}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                xbagCost: e.target.value,
              });
            }}
            placeholder="0.0"
            disabled={isDisabled}
            isInvalid={!!validationFlightFormErrors.xbagCost}
          />
          <Form.Control.Feedback type="invalid">{validationFlightFormErrors.xbagCost}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Flight Cost</Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.5"
            value={safeNumberValue(currentSelectedFlight.flightCost)}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                flightCost: e.target.value,
              });
            }}
            placeholder="0.0"
            disabled={isDisabled}
            isInvalid={!!validationFlightFormErrors.flightCost}
          />
          <Form.Control.Feedback type="invalid">{validationFlightFormErrors.flightCost}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Other Transport Cost</Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.5"
            value={safeNumberValue(currentSelectedFlight.otherTransportCost)}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                otherTransportCost: e.target.value,
              });
            }}
            placeholder="0.0"
            disabled={isDisabled}
          />
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>Hotel Cost</Form.Label>
          <Form.Control
            type="number"
            min="0.0"
            step="0.5"
            value={safeNumberValue(currentSelectedFlight.hotelCost)}
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                hotelCost: e.target.value,
              });
            }}
            placeholder="0.0"
            disabled={isDisabled || isHotelDisabled}
            isInvalid={!!validationFlightFormErrors.hotelCost}
          />
          <Form.Control.Feedback type="invalid">{validationFlightFormErrors.hotelCost}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Cost Center</Form.Label>
          <CreatableSelect
            isDisabled={isDisabled}
            value={
              costCentreOptions.find((option) => option.value === currentSelectedFlight.costCentre) || {
                value: currentSelectedFlight.costCentre,
                label: currentSelectedFlight.costCentre,
              }
            }
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                costCentre: e ? e.value : '',
              });
            }}
            options={costCentreOptions}
            isClearable
            styles={getReactSelectStyles(darkMode)}
          />
          {validationFlightFormErrors.costCentre && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {validationFlightFormErrors.costCentre}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Total Travel Cost</Form.Label>
          <Form.Control
            type="number"
            min="0.0"
            step="0.5"
            value={safeNumberValue(totalCost)}
            placeholder="0.0"
            readOnly
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Currency</Form.Label>
          <CreatableSelect
            isDisabled={isDisabled}
            options={currencySelectOptions}
            isSearchable
            isClearable
            value={
              currencySelectOptions.find((x) => x.value === currentSelectedFlight.currency) ||
              (currentSelectedFlight.currency
                ? { value: currentSelectedFlight.currency, label: currentSelectedFlight.currency }
                : null)
            }
            onChange={(e) => {
              setValidationFlightFormErrors({});
              setCurrentSelectedFlight({
                ...currentSelectedFlight,
                currency: e?.value || '',
              });
            }}
            styles={getReactSelectStyles(darkMode)}
          />
          {validationFlightFormErrors.currency && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {validationFlightFormErrors.currency}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Row>
    </>
  );
};

FlightCostSection.propTypes = {
  currentSelectedFlight: PropTypes.object.isRequired,
  setCurrentSelectedFlight: PropTypes.func.isRequired,
  validationFlightFormErrors: PropTypes.object.isRequired,
  setValidationFlightFormErrors: PropTypes.func.isRequired,
  validLuggageSelectOptions: PropTypes.array.isRequired,
  costCentreOptions: PropTypes.array.isRequired,
  currencySelectOptions: PropTypes.array.isRequired,
  totalCost: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isHotelDisabled: PropTypes.bool,
};

export default FlightCostSection;
